import { useCallback, useEffect, useRef, useState } from "react";

import { downloadReportPdf } from "../api/reportPdf";

/**
 * Drive a report's "Download PDF" button: loading state, double-click guard, error state, and a
 * mounted-ref so a slow request can't set state after the user has navigated away.
 *
 * @param {"audit" | "llm_visibility" | "persona" | "ota_vs_direct" | "perception"} reportType
 * @param {string} reportId
 * @returns {{ isDownloading: boolean; error: string | null; download: () => Promise<void> }}
 */
export function useDownloadReportPdf(reportType, reportId) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const download = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setError(null);
    try {
      await downloadReportPdf(reportType, reportId);
    } catch (err) {
      console.error("Report PDF download failed", err);
      if (isMountedRef.current) {
        setError("Couldn't generate the PDF. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
      }
    }
  }, [isDownloading, reportType, reportId]);

  return { isDownloading, error, download };
}
